import React, { useState } from 'react';
import {
  Typography, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, TextField, Snackbar, Alert, Tooltip,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import { usePlatformIssues } from '../../data/PlatformIssueStore';

interface Props {
  pageName?: string;
}

export default function BugReportButton({ pageName }: Props) {
  const { submitBugReport } = usePlatformIssues();
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState<'success' | 'error' | null>(null);

  const handleSubmit = async () => {
    if (!description.trim() || submitting) return;
    setSubmitting(true);
    const ok = await submitBugReport({
      description: description.trim(),
      pageName: pageName || undefined,
    });
    setSubmitting(false);
    if (ok) {
      setOpen(false);
      setDescription('');
      setSnack('success');
    } else {
      setSnack('error');
    }
  };

  return (
    <>
      <Tooltip title="Report a platform bug" arrow placement="left">
        <IconButton
          onClick={() => setOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 1200,
            width: 48,
            height: 48,
            bgcolor: '#ec4899',
            border: 'none',
            color: '#fff',
            boxShadow: '0 2px 12px rgba(236,72,153,0.35)',
            '&:hover': { bgcolor: '#db2777', boxShadow: '0 4px 16px rgba(236,72,153,0.5)' },
            transition: 'all 0.15s',
          }}
        >
          <BugReportIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', pb: 0.5 }}>
          Report a platform problem
        </DialogTitle>
        <Typography sx={{ px: 3, fontSize: '0.78rem', color: '#64748b', mb: 1 }}>
          Something not working right? Let us know. This is for technical issues with the platform — not for clinical or implementation support.
        </Typography>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            placeholder="Describe what happened, what you expected, and any error messages you saw..."
            value={description}
            onChange={e => setDescription(e.target.value)}
            sx={{ mt: 1, '& .MuiOutlinedInput-root': { fontSize: '0.875rem', borderRadius: '8px' } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ color: '#64748b', textTransform: 'none', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!description.trim() || submitting}
            variant="contained"
            sx={{
              textTransform: 'none', fontWeight: 600, borderRadius: '8px',
              bgcolor: '#64748b', '&:hover': { bgcolor: '#475569' },
            }}
          >
            {submitting ? 'Submitting…' : 'Submit report'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack !== null} autoHideDuration={4000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack === 'error' ? 'error' : 'success'} variant="filled" sx={{ borderRadius: '8px' }}>
          {snack === 'error'
            ? 'Could not submit your report — please try again in a moment.'
            : 'Bug report submitted — thank you!'}
        </Alert>
      </Snackbar>
    </>
  );
}
